import React, { useMemo } from 'react';
import { useLingui } from '@lingui/react/macro';

import Overlay from '@rover/react-lib/src/components/ImageGallery/Overlay';

import type { BulkPrivacyChange } from './hooks/useGalleryManageMode';
import type { GalleryItem, PaginationConfig } from './ImageGallery.common';
import { GalleryViewMode } from './ImageGallery.common';
import ImageGrid from './ImageGrid';
import Lightbox from './Lightbox';

type ImageGalleryProps = {
  images: GalleryItem[] | undefined;
  galleryViewMode: GalleryViewMode;
  lightboxIndex: number;
  setLightboxIndex: React.Dispatch<React.SetStateAction<number>>;
  onClose: () => void;
  onImageClick: (index: number) => void;
  onViewGallery?: () => void;
  pagination?: PaginationConfig;
  renderImageFooter?: (index: number) => React.ReactNode;
  renderImageOverlay?: (index: number) => React.ReactNode;
  dimmedImageIds?: Set<number>;
  lockedImageIds?: Set<number>;
  /**
   * Native only: have the grid scroll itself, so it windows its tiles and loads
   * further pages as the owner scrolls rather than all at once. Worth passing
   * for a gallery that can run to hundreds of photos. See
   * `ImageGridContainer.native`.
   */
  virtualizedGrid?: boolean;
  /** Web only: center the lightbox in the viewport rather than top-aligning it. */
  centerVertically?: boolean;
  /** Whether these photos are the viewer's to curate. */
  isViewingOwnGallery?: boolean;
  onBulkPrivacyChange?: BulkPrivacyChange;
  gridHeader?: React.ReactNode;
};

const ImageGallery = ({
  images,
  galleryViewMode,
  lightboxIndex,
  setLightboxIndex,
  onClose,
  onImageClick,
  onViewGallery,
  pagination,
  renderImageFooter,
  renderImageOverlay,
  dimmedImageIds,
  lockedImageIds,
  virtualizedGrid,
  centerVertically,
  isViewingOwnGallery,
  onBulkPrivacyChange,
  gridHeader,
}: ImageGalleryProps): JSX.Element => {
  const { t } = useLingui();

  const dimmedOrLockedImageIds = useMemo(() => {
    if (!lockedImageIds?.size) return dimmedImageIds;
    return new Set([...(dimmedImageIds ?? []), ...lockedImageIds]);
  }, [dimmedImageIds, lockedImageIds]);

  if (galleryViewMode === GalleryViewMode.Grid) {
    return (
      <Overlay collapseMobilePx={false} onClose={onClose} ariaLabel={t`Image gallery`}>
        <ImageGrid
          images={images}
          onClose={onClose}
          onImageClick={onImageClick}
          pagination={pagination}
          renderImageOverlay={renderImageOverlay}
          virtualizedGrid={virtualizedGrid}
          dimmedImageIds={dimmedImageIds}
          lockedImageIds={lockedImageIds}
          isViewingOwnGallery={isViewingOwnGallery}
          onBulkPrivacyChange={onBulkPrivacyChange}
          gridHeader={gridHeader}
        />
      </Overlay>
    );
  }

  return (
    <Overlay
      collapseMobilePx
      onClose={onClose}
      ariaLabel={t`Image gallery`}
      centerVertically={centerVertically}
    >
      <Lightbox
        images={images}
        lightboxIndex={lightboxIndex}
        setLightboxIndex={setLightboxIndex}
        onClose={onClose}
        onViewGallery={onViewGallery}
        pagination={pagination}
        renderImageFooter={renderImageFooter}
        dimmedImageIds={dimmedOrLockedImageIds}
      />
    </Overlay>
  );
};

export default ImageGallery;
